import { createTheme, Palette, Theme as MuiTheme, ThemeProvider } from '@mui/material/styles';
import React, { useMemo } from 'react';
import { enUS, ptBR } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { arSA } from '@mui/material/locale';

export interface CustomPalette extends Palette {
  appColors: {
    white: string;
    yellow: string;
    orange: string;
  };
}

export interface CustomTheme extends Omit<MuiTheme, 'palette'> {
  palette: CustomPalette;
  borderStyles: {
    authCardBorders: string;
    cardBorders: string;
    tabBarBorders: string;
    commonBorders: string;
  };
}

declare module '@mui/material/styles' {
  interface TypographyVariants {
    bodyBold: React.CSSProperties;
    bodyRegular: React.CSSProperties;
    meta: React.CSSProperties;
    tinyTexts: React.CSSProperties;
    title1Bold: React.CSSProperties;
    title2Bold: React.CSSProperties;
    title3Regular: React.CSSProperties;
    calloutRegular: React.CSSProperties;
    caption2Bold: React.CSSProperties;
    titleLargeBold: React.CSSProperties;
    caption2Regular: React.CSSProperties;
    subheadBold: React.CSSProperties;
    subheadRegular: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    bodyBold?: React.CSSProperties;
    bodyRegular?: React.CSSProperties;
    meta?: React.CSSProperties;
    tinyTexts?: React.CSSProperties;
    title1Bold?: React.CSSProperties;
    title2Bold?: React.CSSProperties;
    title3Regular?: React.CSSProperties;
    calloutRegular?: React.CSSProperties;
    caption2Bold?: React.CSSProperties;
    titleLargeBold?: React.CSSProperties;
    caption2Regular?: React.CSSProperties;
    subheadBold?: React.CSSProperties;
    subheadRegular?: React.CSSProperties;
  }

  interface PaletteOptions {
    appColors: {
      white: string;
      yellow: string;
      orange: string;
    };
  }

  interface BorderStyles {
    authCardBorders: string;
    cardBorders: string;
    tabBarBorders: string;
    commonBorders: string;
  }

  interface Theme {
    borderStyles: BorderStyles;
  }

  interface ThemeOptions {
    borderStyles?: Partial<BorderStyles>;
    tabBarBorders?: string;
    commonBorders?: string;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    bodyBold: true;
    bodyRegular: true;
    tinyTexts: true;
    meta: true;
    title1Bold: true;
    title2Bold: true;
    title3Regular: true;
    calloutRegular: true;
    subheadBold: true;
    subheadRegular: true;
    caption2Bold: true;
    titleLargeBold: true;
    caption2Regular: true;
  }
}

interface IThemeWrapperProps {
  children: React.ReactNode;
}

const customBreakpoints = {
  values: {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1280,
    llg: 1600,
    xl: 1920,
  },
};

export default function ThemeWrapper({ children }: IThemeWrapperProps) {
  const {
    i18n: { language },
  } = useTranslation();

  const resolveLanguageBundle = () => {
    const langCode = language.split('-')[0];
    switch (langCode) {
      case 'en':
        return enUS;
      case 'pt':
        return ptBR;
      case 'ar':
        return arSA;
      default:
        return enUS;
    }
  };

  // Had to take theme into a useMemo since we are now handling locale changes
  // to MUI component in one central place and it needs to be supplied with the
  // createTheme function. So everytime the locale changes, we need to create
  // a new theme object.
  const theme = useMemo(
    () =>
      createTheme(
        {
          direction: language.split('-')[0] === 'ar' ? 'rtl' : 'ltr',
          breakpoints: customBreakpoints,
          typography: {
            fontFamily: 'satoshi',
            h1: {
              fontFamily: 'Satoshi',
              fontWeight: 500,
              fontSize: '61px',
              lineHeight: '120%',
            },
            h2: {
              fontFamily: 'Satoshi',
              fontWeight: 500,
              fontSize: '49px',
              lineHeight: '120%',
            },
            h3: {
              fontFamily: 'Satoshi',
              fontWeight: 500,
              fontSize: '39px',
              lineHeight: '120%',
            },
            titleLargeBold: {
              fontFamily: 'Satoshi',
              fontWeight: 700,
              fontSize: '34px',
              lineHeight: '41px',
            },
            h4: {
              fontFamily: 'Satoshi',
              fontWeight: 700,
              fontSize: '28px',
              lineHeight: '34px',
            },
            h5: {
              fontFamily: 'Satoshi',
              fontWeight: 500,
              fontSize: '25px',
              lineHeight: '120%',
            },
            title1Bold: {
              fontFamily: 'Satoshi',
              fontWeight: 700,
              fontSize: '28px',
              lineHeight: '28px',
            },
            title2Bold: {
              fontFamily: 'Satoshi',
              fontWeight: 700,
              fontSize: '22px',
              lineHeight: '28px',
            },
            h6: {
              fontFamily: 'Satoshi',
              fontWeight: 700,
              fontSize: '20px',
              lineHeight: '125%',
            },
            title3Regular: {
              fontFamily: 'Satoshi',
              fontWeight: 400,
              fontSize: '20px',
              lineHeight: '125%',
            },
            bodyBold: {
              fontFamily: 'satoshi',
              fontWeight: 700,
              fontSize: '17px',
              lineHeight: '22px',
            },
            bodyRegular: {
              fontFamily: 'satoshi',
              fontWeight: 400,
              fontSize: '17px',
              lineHeight: '22px',
            },
            calloutRegular: {
              fontFamily: 'satoshi',
              fontWeight: 500,
              fontSize: '16px',
              lineHeight: '21px',
            },
            subheadBold: {
              fontFamily: 'satoshi',
              fontWeight: 700,
              fontSize: '15px',
              lineHeight: '20px',
            },
            subheadRegular: {
              fontFamily: 'satoshi',
              fontWeight: 400,
              fontSize: '15px',
              lineHeight: '20px',
            },
            button: {
              fontFamily: 'satoshi',
              fontWeight: 500,
              fontSize: '15px',
              lineHeight: '20px',
            },
            meta: {
              fontFamily: 'Satoshi',
              fontWeight: 400,
              fontSize: '13px',
              lineHeight: '18px',
            },
            tinyTexts: {
              fontFamily: 'Satoshi',
              fontWeight: 400,
              fontSize: '12px',
              lineHeight: '16px',
            },
            caption2Regular: {
              fontFamily: 'Satoshi',
              fontWeight: 400,
              fontSize: '11px',
              lineHeight: '13px',
            },
            caption2Bold: {
              fontFamily: 'Satoshi',
              fontWeight: 500,
              fontSize: '11px',
              lineHeight: '13px',
            },
          },
          palette: {
            primary: {
              main: '#2A3C67',
              dark: '#001E1D',
              light: '#EDF6F4',
            },
            secondary: {
              main: '#8DACAA',
              dark: '#ABD1C6',
              light: '#E5E8E8',
            },
            text: {
              primary: '#001E1D',
              secondary: '#8DACAA',
              disabled: '#E16162',
            },
            appColors: {
              white: '#FFFFFE',
              yellow: '#F9BC60',
              orange: '#E16162',
            },
            warning: {
              main: '#E16162',
            },
          },
          components: {
            MuiButton: {
              defaultProps: {
                disableRipple: true,
                disableElevation: true,
                style: {
                  textTransform: 'capitalize',
                  padding: '12px 32px',
                },
              },
              // add global styles to all Button components
              styleOverrides: {},
            },
            // mui tab styles
            MuiTab: {
              defaultProps: {
                disableRipple: true,
              },
              styleOverrides: {
                root: {
                  textTransform: 'none',
                  // change the color of the tab text
                  // color: '#004643',
                  // change the color of the indicator
                  borderBottom: '5px',
                  // '&.Mui-selected': {
                  //   color: '#004643',
                  // },
                },
              },
            },
            MuiTabs: {
              defaultProps: {},
              styleOverrides: {
                root: {
                  borderBottom: 'none',
                },
              },
            },
            MuiTableCell: {
              defaultProps: {},
              styleOverrides: {
                root: {
                  border: 'none',
                },
              },
            },
            MuiTableRow: {
              defaultProps: {},
              styleOverrides: {
                root: {
                  border: 'none',
                },
              },
            },
            MuiTable: {
              defaultProps: {},
              styleOverrides: {
                root: {
                  border: 'none',
                },
              },
            },
          },
          borderStyles: {
            // for cards used in dashboard
            authCardBorders: '0px 4px 40px 0px rgba(255, 181, 171, 0.15)',
            cardBorders: '0px 4px 40px 0px rgba(255, 221, 200, 0.25)',
            tabBarBorders: '1px solid  rgba(171, 209, 198, 0.20)',
            commonBorders: '1px solid var(--Accent-Unbleached-Silk, #FFDDC8)',
          },
        },
        resolveLanguageBundle()
      ),
    [language]
  );

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}
